import ItemStatus from "../../../utils/ItemStatus";
import { toDay } from "../charts/charts.utils";

import type {
  GetUnsubscribeReasonsResponse,
  OfferPurchase,
  OfferSubscription,
  OfferTrialUnsubscriptionViews,
  OfferUnsubscription,
  OfferUnsubscriptionGroupByOffer,
} from "@/src/api/StatsAPI.types";

import type {
  CountSeriesKey,
  MissingOfferLabelResolver,
  OfferIndexSource,
  OfferOption,
  SalesSeriesKey,
  SalesSummary,
  SubscriptionSeriesKey,
  TopReason,
  UnsubscriptionSeriesKey,
  UnsubscriptionsSummary,
} from "./offerAnalytics.types";
import type { SubscriptionRow } from "./tableParams/subscriptionColumns.types";
import type { RankedDatum, TimelineDatum } from "../charts/charts.types";

export const SUBSCRIPTION_SERIES_KEY_LIST: readonly SubscriptionSeriesKey[] = [
  "new",
  "gift",
  "freecopy",
];
export const SALES_SERIES_KEY_LIST: readonly SalesSeriesKey[] = [
  "direct",
  "gifts",
];
export const UNSUBSCRIPTION_SERIES_KEY_LIST: readonly UnsubscriptionSeriesKey[] =
  ["voluntary", "automatic"];
export const COUNT_SERIES_KEY_LIST: readonly CountSeriesKey[] = ["count"];

const UNKNOWN_OFFER_KEY = "__unknown_offer__";

// The API can return null offers or offer fields despite its declared types.
interface NullableOffer {
  label?: string | null;
}

function getOfferLabel(offer: NullableOffer | null | undefined): string | null {
  return offer?.label ?? null;
}

function isFilled(value: string | null | undefined): value is string {
  return typeof value === "string" && value.length > 0;
}

function toOfferKey(uuid: string | null | undefined): string {
  return isFilled(uuid) ? uuid : UNKNOWN_OFFER_KEY;
}

function toOfferLabelEntry(
  uuid: string | null | undefined,
  label: string | null | undefined,
  getMissingOfferLabel: MissingOfferLabelResolver,
): [string, string] {
  return [
    toOfferKey(uuid),
    isFilled(label)
      ? label
      : getMissingOfferLabel(isFilled(uuid) ? uuid : null),
  ];
}

function sumByGroup<TItem, TKey extends string>(
  itemList: readonly TItem[],
  keyList: readonly TKey[],
  getGroup: (item: TItem) => string,
  getValues: (item: TItem) => Record<TKey, number>,
): Map<string, Record<TKey, number>> {
  const totalsByGroup = new Map<string, Record<TKey, number>>();

  itemList.forEach((item) => {
    const group = getGroup(item);
    const values = getValues(item);
    const totals = totalsByGroup.get(group);

    if (!totals) {
      totalsByGroup.set(group, { ...values });
      return;
    }
    keyList.forEach((key) => {
      totals[key] += values[key];
    });
  });

  return totalsByGroup;
}

function toTimeline<TKey extends string>(
  totalsByDate: Map<string, Record<TKey, number>>,
): TimelineDatum<TKey>[] {
  return [...totalsByDate]
    .map(([date, values]) => ({ date, values }))
    .sort((first, second) => first.date.localeCompare(second.date));
}

function toRanked<TKey extends string>(
  totalsByGroup: Map<string, Record<TKey, number>>,
  labelByGroup: Map<string, string>,
  keyList: readonly TKey[],
): RankedDatum<TKey>[] {
  return [...totalsByGroup]
    .filter(([, values]) => keyList.some((key) => values[key] > 0))
    .map(([group, values]) => ({
      label: labelByGroup.get(group) ?? group,
      values,
    }));
}

function sumBy<TItem>(
  itemList: readonly TItem[],
  getValue: (item: TItem) => number,
): number {
  return itemList.reduce((sum, item) => sum + getValue(item), 0);
}

export function buildSubscriptionTimeline(
  subscriptionList: readonly OfferSubscription[],
): TimelineDatum<SubscriptionSeriesKey>[] {
  return toTimeline(
    sumByGroup(
      subscriptionList,
      SUBSCRIPTION_SERIES_KEY_LIST,
      (subscription) => toDay(subscription.date),
      ({ subscriptions }) => ({
        new: subscriptions.subscription_new.count,
        gift: subscriptions.subscription_gift.count,
        freecopy: subscriptions.subscription_freecopy.count,
      }),
    ),
  );
}

export function sumNewSubscriptions(
  subscriptionList: readonly OfferSubscription[],
): number {
  return sumBy(
    subscriptionList,
    (subscription) => subscription.subscriptions.subscription_new.count,
  );
}

export function buildSubscriptionsByOffer(
  subscriptionList: readonly OfferSubscription[],
  getMissingOfferLabel: MissingOfferLabelResolver,
): RankedDatum<CountSeriesKey>[] {
  return toRanked(
    sumByGroup(
      subscriptionList,
      COUNT_SERIES_KEY_LIST,
      (subscription) => toOfferKey(subscription.offer_uuid),
      (subscription) => ({
        count: subscription.subscriptions.subscription_new.count,
      }),
    ),
    new Map(
      subscriptionList.map((subscription) =>
        toOfferLabelEntry(
          subscription.offer_uuid,
          getOfferLabel(subscription.offer),
          getMissingOfferLabel,
        ),
      ),
    ),
    COUNT_SERIES_KEY_LIST,
  );
}

export function filterByOffer<TItem>(
  itemList: readonly TItem[],
  offerUuid: string | undefined,
  getItemOfferUuid: (item: TItem) => string | null | undefined,
): readonly TItem[] {
  if (!offerUuid) return itemList;
  return itemList.filter((item) => getItemOfferUuid(item) === offerUuid);
}

export function summarizePurchases(
  purchaseList: readonly OfferPurchase[],
): SalesSummary {
  return {
    total: sumBy(purchaseList, (purchase) => purchase.total_sales),
    direct: sumBy(purchaseList, (purchase) => purchase.direct_sales),
    gifts: sumBy(purchaseList, (purchase) => purchase.gifts),
  };
}

export function buildSalesByOffer(
  purchaseList: readonly OfferPurchase[],
  getMissingOfferLabel: MissingOfferLabelResolver,
): RankedDatum<SalesSeriesKey>[] {
  return toRanked(
    sumByGroup(
      purchaseList,
      SALES_SERIES_KEY_LIST,
      (purchase) => toOfferKey(purchase.offer_uuid),
      (purchase) => ({ direct: purchase.direct_sales, gifts: purchase.gifts }),
    ),
    new Map(
      purchaseList.map((purchase) =>
        toOfferLabelEntry(
          purchase.offer_uuid,
          getOfferLabel(purchase.offer),
          getMissingOfferLabel,
        ),
      ),
    ),
    SALES_SERIES_KEY_LIST,
  );
}

export function buildUnsubscriptionTimeline(
  unsubscriptionList: readonly OfferUnsubscriptionGroupByOffer[],
): TimelineDatum<UnsubscriptionSeriesKey>[] {
  return toTimeline(
    sumByGroup(
      unsubscriptionList,
      UNSUBSCRIPTION_SERIES_KEY_LIST,
      (unsubscription) => toDay(unsubscription.date),
      (unsubscription) => ({
        voluntary: unsubscription.unsubscribers,
        automatic: unsubscription.automatic_unsubscribers,
      }),
    ),
  );
}

export function summarizeUnsubscriptions(
  unsubscriptionList: readonly OfferUnsubscriptionGroupByOffer[],
): UnsubscriptionsSummary {
  const voluntary = sumBy(
    unsubscriptionList,
    (unsubscription) => unsubscription.unsubscribers,
  );
  const automatic = sumBy(
    unsubscriptionList,
    (unsubscription) => unsubscription.automatic_unsubscribers,
  );
  return { total: voluntary + automatic, voluntary, automatic };
}

export function buildUnsubscriptionsByOffer(
  unsubscriptionList: readonly OfferUnsubscription[],
  getMissingOfferLabel: MissingOfferLabelResolver,
): RankedDatum<CountSeriesKey>[] {
  return toRanked(
    sumByGroup(
      unsubscriptionList,
      COUNT_SERIES_KEY_LIST,
      (unsubscription) => toOfferKey(unsubscription.offer_uuid),
      (unsubscription) => ({
        count:
          unsubscription.unsubscribers + unsubscription.automatic_unsubscribers,
      }),
    ),
    new Map(
      unsubscriptionList.map((unsubscription) =>
        toOfferLabelEntry(
          unsubscription.offer_uuid,
          getOfferLabel(unsubscription.offer),
          getMissingOfferLabel,
        ),
      ),
    ),
    COUNT_SERIES_KEY_LIST,
  );
}

export function buildTrialTimeline(
  trialList: readonly OfferTrialUnsubscriptionViews[],
): TimelineDatum<CountSeriesKey>[] {
  return toTimeline(
    sumByGroup(
      trialList,
      COUNT_SERIES_KEY_LIST,
      (trial) => toDay(trial.date),
      (trial) => ({ count: trial.count }),
    ),
  );
}

export function sumTrialUnsubscriptions(
  trialList: readonly OfferTrialUnsubscriptionViews[],
): number {
  return sumBy(trialList, (trial) => trial.count);
}

export function buildUnsubscribeReasons(
  response: GetUnsubscribeReasonsResponse | undefined,
  lang: string,
  translateReason: (reason: string) => string,
): RankedDatum<CountSeriesKey>[] {
  const reasons = response?.data?.reasons;
  if (!reasons) return [];

  const labelByReason = new Map(
    (response.translate_reasons?.reasons ?? []).map(
      ({ name, translations }) => {
        const [fallbackLang] = Object.keys(translations);
        return [
          name,
          translations[lang] ?? translations[fallbackLang ?? ""] ?? name,
        ];
      },
    ),
  );

  return Object.entries(reasons)
    .filter(([, count]) => count > 0)
    .map(([reason, count]) => ({
      label: labelByReason.get(reason) ?? translateReason(reason),
      values: { count },
    }));
}

export function getTopReason(
  reasonList: readonly RankedDatum<CountSeriesKey>[],
): TopReason | null {
  const total = sumBy(reasonList, (reason) => reason.values.count);
  const [firstReason, ...otherReasonList] = reasonList;
  if (!firstReason || total === 0) return null;

  const topReason = otherReasonList.reduce(
    (best, reason) => (reason.values.count > best.values.count ? reason : best),
    firstReason,
  );
  return { label: topReason.label, share: topReason.values.count / total };
}

export function getProjectOfferOptions(
  offers: OfferIndexSource,
  projectUuid: string,
): OfferOption[] {
  const indexList = offers.indexes.storefront_project_uuid?.[projectUuid] ?? [];

  return indexList.flatMap((index) => {
    const offer = offers.data[index];
    return offer ? [{ value: offer.uuid, label: offer.label }] : [];
  });
}

export function getPurchasePrice(purchase: OfferPurchase): number | null {
  if (purchase.offer_plan_pricing) return purchase.offer_plan_pricing;
  if (purchase.media_pricing) return purchase.media_pricing;
  return null;
}

/** Une ligne par offre, la plus récente d'abord (date de sa première ligne). */
export function groupByOffer(lines: OfferSubscription[]): SubscriptionRow[] {
  const rowByOffer = new Map<string, SubscriptionRow>();
  const firstLineTimes = new Map<string, number>();
  lines.forEach((line) => {
    const { subscriptions } = line;
    const sales =
      subscriptions.subscription_new.count +
      subscriptions.subscription_canceled.count;
    const gift = subscriptions.subscription_gift.count;
    const freecopy = subscriptions.subscription_freecopy.count;
    const row = rowByOffer.get(line.offer_uuid);
    if (row) {
      row.sales += sales;
      row.total_subscription_gift += gift;
      row.total_subscription_freecopy += freecopy;
    } else {
      firstLineTimes.set(line.offer_uuid, Date.parse(line.date));
      rowByOffer.set(line.offer_uuid, {
        uuid: line.offer_uuid,
        status: ItemStatus.getOfferStatus(line.offer),
        offer_label: line.offer.label,
        sales,
        total_subscription_gift: gift,
        total_subscription_freecopy: freecopy,
      });
    }
  });
  const firstLineTime = (row: SubscriptionRow): number =>
    firstLineTimes.get(row.uuid) ?? 0;
  return [...rowByOffer.values()].sort(
    (firstRow, secondRow) => firstLineTime(secondRow) - firstLineTime(firstRow),
  );
}
